import type { UiNode, UiNodeInputAttributes } from '@ory/client';
import { useState, useId, useMemo } from 'react';
import { Checkbox } from '@/components/Checkbox';
import { FormItem } from '@/components/FormItem';
import Input from '@/components/Input/Input';
import { HStack, VStack } from '@/components/Layout';
import { Label } from '@/components/ui/label';
import { PASSWORD_PATTERN, PASSWORD_REGEX } from '@/features/account/components/ChangePasswordDialog/schema';
import { useNodeTranslation } from '@/features/auth/hooks/useNodeTranslation';
import { defaultNS, useTranslation } from '@/lib/i18n';

/** 表示順序: 入力フィールドは中間に配置 */
export const ORDER = 50;
type Props = {
  node: UiNode;
  disabled?: boolean;
};

/**
 * Password inputノードコンポーネント
 *
 * Ory KratosのServer Driven UIで返されるpassword inputフィールドをレンダリングします。
 * パスワード表示/非表示機能付きのパスワード入力フィールドを表示します。
 * PasswordFieldコンポーネントと同じUI構造を使用します。
 *
 * パスワード強度バリデーション:
 * - autocomplete="new-password" の場合のみ適用（パスワード設定・変更時）
 * - autocomplete="current-password" の場合は適用しない（サインイン時）
 *
 * @see https://www.ory.com/docs/kratos/concepts/ui-user-interface#ui-input-nodes
 */
export function PasswordInput({
  node,
  disabled
}: Props) {
  const {
    translateUiText
  } = useNodeTranslation();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.auth'
  });
  const attrs = node.attributes as UiNodeInputAttributes;

  // 新しいパスワードを設定する場合のみバリデーションを適用
  const shouldValidateStrength = attrs.autocomplete === 'new-password';

  // フックは常に同じ順序で呼ばれる必要があるため、早期リターンの前に配置
  const [visible, setVisible] = useState<boolean>(false);
  const [value, setValue] = useState<string>(attrs.value as string || '');
  const [touched, setTouched] = useState<boolean>(false);
  const showPasswordId = useId();

  // パスワード強度バリデーション
  const isPasswordValid = useMemo(() => {
    if (!shouldValidateStrength || !value) {
      return true;
    }
    return PASSWORD_REGEX.test(value);
  }, [shouldValidateStrength, value]);

  // password inputの場合はtype="password"である必要がある
  if (attrs.type !== 'password') {
    return null;
  }
  const label = node.meta?.label ? translateUiText(node.meta.label) : undefined;

  // サーバーからのエラーメッセージ
  const serverErrorMessages = node.messages?.filter(msg => msg.type === 'error').map(msg => translateUiText(msg));

  // フロントエンドのバリデーションエラーメッセージ
  const frontendValidationError = shouldValidateStrength && touched && value && !isPasswordValid ? t('passwordStrengthError') : undefined;

  // 表示するエラーメッセージ（サーバーエラーを優先、なければフロントエンドバリデーション）
  const displayErrorMessage = serverErrorMessages?.[0] ?? frontendValidationError;
  const hasError = Boolean(serverErrorMessages?.length) || Boolean(frontendValidationError);
  return <FormItem fieldTitle={label} optional={!attrs.required} errorMessage={displayErrorMessage} className="w-full" fieldInput={<VStack className="gap-1.5">
          <Input type={visible ? 'text' : 'password'} name={attrs.name} value={value} onChange={e => setValue(e.target.value)} onBlur={() => setTouched(true)} placeholder={(attrs as {
      placeholder?: string;
    }).placeholder} disabled={disabled ?? attrs.disabled} required={attrs.required} autoComplete={attrs.autocomplete as string | undefined} pattern={shouldValidateStrength ? PASSWORD_PATTERN : undefined} title={shouldValidateStrength ? t('passwordStrengthError') as string : undefined} width="100%" error={hasError} />
          <HStack className="items-center gap-2 pl-3">
            <Checkbox id={showPasswordId} checked={visible} onCheckedChange={setVisible} />
            <Label htmlFor={showPasswordId} className="text-xs">
              {t('showPassword')}
            </Label>
          </HStack>
        </VStack>} />;
}